import Heading from "@commons/Typography/Heading";
import Text from "@commons/Typography/Text";
import Button from "@commons/Button";
import AttachmentPreview from "@commons/Preview/AttachmentPreview";
import ListPreview from "@commons/Preview/ListPreview";
import { usePage } from "@hooks/usePage";
import type { Individual } from "@typings/entities/individuals";
import { CircularProgress } from "@mui/material";

import {
  getGenderLabel,
  getMarkingTypeLabel,
  getSituationLabel,
} from "@utils/translator/individuals";
import { toast } from "react-toastify";
import { formatDate } from "@utils/formatters";
import { useGetIndividualAttachments } from "@services/individuals/getAttachmentsById";
import { useState } from "react";
import { config } from "@/config";
import { usePermissions } from "@hooks/usePermissions";

function prepareInfoCardsIndividual(
  individual?: Individual,
): Individual | undefined {
  if (!individual) return undefined;

  return {
    ...individual,
    lastEntranceDate: formatDate(individual.lastEntranceDate) || "",
    lastReleaseDate: formatDate(individual.lastReleaseDate ?? null) || "",
    situation: getSituationLabel(individual.situation ?? "") || "",
    gender: getGenderLabel(individual.gender ?? "") || "",
    markingType: getMarkingTypeLabel(individual.markingType ?? "") || "",
  };
}

interface IndividualInfoCardProps {
  individual?: Individual | null;
}

function IndividualInfoCard({ individual }: IndividualInfoCardProps) {
  const { openModal, setDataModal } = usePage();
  const [currentPage, setCurrentPage] = useState(1);
  const { can } = usePermissions();

  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    error: attachmentsError,
  } = useGetIndividualAttachments(individual?.id || "", {
    page: currentPage,
    limit: config.attachmentsGallery.defaultPageSize,
  });

  const processedIndividual = prepareInfoCardsIndividual(
    individual || undefined,
  );

  if (!processedIndividual) {
    toast.error("Indivíduo não encontrado.");
    return null;
  }

  const {
    clinicalRecord,
    lastEntranceDate,
    commonName,
    taxonomicGroup,
    ufuName,
    gender,
    markingType,
    marking,
    situation,
    observations,
  } = processedIndividual;

  const canUpdateIndividual = can("update", "individuals");

  return (
    <div className="w-full h-full bg-white flex justify-start items-start flex-col gap-3 p-4 shadow-md rounded-2xl">
      <Heading className="font-roboto">Informações do Indivíduo</Heading>

      <ListPreview.Root>
        <ListPreview.Row label="N° Ficha:" value={clinicalRecord || "—"} />
        <ListPreview.Row
          label="Data de entrada:"
          value={lastEntranceDate || "—"}
        />
        <ListPreview.Row label="Nome Comum:" value={commonName || "—"} />
        <ListPreview.Row
          label="Nome Científico:"
          value={taxonomicGroup?.species || "—"}
        />
        <ListPreview.Row label="Nome UFU:" value={ufuName || "—"} />
        <ListPreview.Row label="Sexo:" value={gender || "—"} />
        <ListPreview.Row label="Tipo de marcação:" value={markingType || "—"} />
        <ListPreview.Row label="Marcação:" value={marking || "—"} />
        <ListPreview.Row label="Situação do animal:" value={situation || "—"} />
      </ListPreview.Root>

      <div className="w-full">
        <Text size="sm" className="font-roboto mb-1">
          Anexos:
        </Text>

        {isLoadingAttachments && (
          <div className="flex justify-center items-center py-4">
            <CircularProgress size={24} className="text-primary" />
            <Text size="sm" className="ml-2 text-gray-500">
              Carregando anexos...
            </Text>
          </div>
        )}

        {attachmentsError && (
          <div className="flex items-center py-2">
            <Text size="sm" className="text-red-500">
              Erro ao carregar anexos. Tente novamente.
            </Text>
          </div>
        )}

        {!isLoadingAttachments &&
          !attachmentsError &&
          attachments &&
          attachments?.items.length > 0 && (
            <AttachmentPreview
              attachments={attachments.items}
              pagination={{
                pageSize: config.attachmentsGallery.defaultPageSize,
                currentPage: attachments.meta.currentPage,
                totalPages: attachments.meta.totalPages,
                totalItems: attachments.meta.totalItems,
                onPageChange: setCurrentPage,
              }}
            />
          )}

        {!isLoadingAttachments &&
          !attachmentsError &&
          (!attachments || attachments?.items.length === 0) && (
            <Text size="sm" className="text-gray-500 py-2">
              Nenhum anexo encontrado.
            </Text>
          )}
      </div>

      <div className="w-full flex-1 flex flex-col min-h-0 max-h-[36rem] overflow-y-auto">
        <Text size="sm" className="font-roboto mb-1 flex-shrink-0">
          Observações:
        </Text>

        {observations ? (
          <div className="flex-1 overflow-y-auto space-y-4 p-4 border border-gray-200 rounded-md bg-gray-50 font-roboto text-sm text-gray-700">
            {observations
              .split(/Data:/)
              .filter((obs) => obs.trim() !== "")
              .map((obs, index) => {
                const [data, texto] = obs.split("Observação:");
                return (
                  <div
                    key={index}
                    className="border-l-4 border-primary pl-3 hover:bg-green-50 transition-colors rounded"
                  >
                    <p className="font-medium text-gray-800">
                      Data: {data?.trim()}
                    </p>
                    <p className="text-gray-600">Observação: {texto?.trim()}</p>
                  </div>
                );
              })}
          </div>
        ) : (
          <Text size="sm" className="text-gray-500">
            Nenhuma observação registrada.
          </Text>
        )}
      </div>

      <div className="w-full flex justify-center xs:justify-end gap-2 flex-wrap mt-auto">
        {canUpdateIndividual && (
          <>
            <Button
              size="auto"
              customVariant="muted"
              onClick={() => {
                setDataModal({ id: individual?.id });
                openModal("editIndividual");
              }}
            >
              <Text size="sm">Editar Indivíduo</Text>
            </Button>
            <Button
              size="auto"
              customVariant="primary"
              onClick={() => openModal("newDataIndividual")}
            >
              <Text size="sm">Adicionar Dados</Text>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default IndividualInfoCard;
